import { useEffect, useMemo, useState, cloneElement, useId } from "react";
import { Link } from "react-router-dom";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useUser } from "@/lib/userContext";
import SiteHeader from "@/components/SiteHeader";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ArrowRight, Lock } from "lucide-react";

const RAZORPAY_SCRIPT_URL = "https://checkout.razorpay.com/v1/checkout.js";

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const existing = document.querySelector(`script[src="${RAZORPAY_SCRIPT_URL}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () => resolve(false));
      return;
    }
    const s = document.createElement("script");
    s.src = RAZORPAY_SCRIPT_URL;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function CheckoutPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { userId } = useUser();
  const [project, setProject] = useState(null);
  const [meta, setMeta] = useState(null);
  const [product, setProduct] = useState("classic");
  const [addons, setAddons] = useState({ gift_box: false, extra_copy: 0, express_delivery: false, handwritten_note: false, digital_pdf: false });
  const [totals, setTotals] = useState(null);
  const [coupon, setCoupon] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [totalsError, setTotalsError] = useState(false);

  const [form, setForm] = useState({
    customer_name: "", phone: "", email: "", shipping_address: "",
    recipient_name: "", gift_note: "",
  });

  useEffect(() => {
    (async () => {
      try {
      const [m, p] = await Promise.all([api.get("/meta/options"), api.get(`/projects/${projectId}`)]);
      setMeta(m.data);
      setProject(p.data);
      const stored = sessionStorage.getItem(`ks_addons_${projectId}`);
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          setProduct(m.data.products[parsed.product] ? parsed.product : "classic");
          if (parsed.addons) setAddons(parsed.addons);
        } catch (_) { setProduct("classic"); }
      } else {
        setProduct(m.data.products[p.data?.data?.selected_product] ? p.data.data.selected_product : "classic");
      }
      setForm((f) => ({ ...f, recipient_name: p.data?.data?.recipient_name || "" }));
      } catch (_) { setLoadError(true); }
    })();
  }, [projectId]);

  useEffect(() => {
    if (!meta) return;
    const controller = new AbortController();
    setTotals(null); setTotalsError(false);
    const params = new URLSearchParams({
      product_type: product, coupon_code: coupon,
      gift_box: addons.gift_box, extra_copy: addons.extra_copy,
      express_delivery: addons.express_delivery, handwritten_note: addons.handwritten_note, digital_pdf: addons.digital_pdf,
    });
    api.post(`/orders/preview-totals?${params.toString()}`, null, { signal: controller.signal }).then(({ data }) => setTotals(data)).catch((error) => { if (error.code !== "ERR_CANCELED") setTotalsError(true); });
    return () => controller.abort();
  }, [product, addons, coupon, meta]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const placeOrder = async () => {
    if (!totals) return;
    if (!document.getElementById("checkout-form").reportValidity()) return;
    if (!form.customer_name || !form.email || !form.phone || !form.shipping_address) {
      toast.error("Please fill name, email, phone and shipping address.");
      return;
    }
    setSubmitting(true);
    try {
      // Step 1: ask backend to create a Razorpay order
      const { data: rzpOrder } = await api.post("/payments/create-order", {
        user_id: userId,
        project_id: projectId,
        product_type: product,
        addons,
        coupon_code: coupon,
      });

      // Step 2: load Razorpay script
      const loaded = await loadRazorpayScript();
      if (!loaded || !window.Razorpay) {
        toast.error("Could not load payment gateway. Check your connection and try again.");
        setSubmitting(false);
        return;
      }

      // Step 3: open Razorpay Checkout modal
      const options = {
        key: rzpOrder.razorpay_key_id,
        amount: rzpOrder.amount_paise,
        currency: rzpOrder.currency,
        name: "Keepsake Books",
        description: meta.products[product].label,
        order_id: rzpOrder.razorpay_order_id,
        prefill: {
          name: form.customer_name,
          email: form.email,
          contact: form.phone,
        },
        theme: { color: "#D94B37" },
        modal: {
          ondismiss: () => {
            setSubmitting(false);
            toast.info("Payment cancelled.");
          },
        },
        handler: async (response) => {
          // Step 4: verify signature on the server and create Keepsake order
          try {
            const { data: order } = await api.post("/payments/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              user_id: userId,
              project_id: projectId,
              product_type: product,
              addons,
              coupon_code: coupon,
              ...form,
            });
            toast.success("Payment received. Order confirmed!");
            navigate(`/order/${order.id}`);
          } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not verify payment. Please contact support with your payment ID.");
            setSubmitting(false);
          }
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        toast.error(`Payment failed: ${resp.error?.description || "unknown error"}`);
        setSubmitting(false);
      });
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start payment.");
      setSubmitting(false);
    }
  };

  if (loadError) return <><SiteHeader/><main id="main-content" className="form-shell"><p role="alert" data-testid="checkout-load-error">We couldn’t load checkout.</p><button onClick={() => window.location.reload()} className="btn-primary mt-5" data-testid="checkout-retry">Try again</button></main></>;
  if (!project || !meta) return (
    <div className="min-h-screen bg-cream">
      <SiteHeader />
      <div className="max-w-3xl mx-auto px-4 py-20 text-center"><p className="font-serif text-2xl text-ink">Loading…</p></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-cream">
      <SiteHeader showNav={false} />
      <main id="main-content" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <p className="k-overline mb-3">Checkout</p>
        <h1 className="page-title" data-testid="checkout-heading">A little closer to being theirs.</h1>
        <p className="text-sm text-inkMute mt-4">Confirm your details and total before opening Razorpay.</p>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
          <form id="checkout-form" onSubmit={(e) => { e.preventDefault(); placeOrder(); }} className="lg:col-span-2 space-y-6" data-testid="checkout-form">
            {/* Customer info */}
            <div className="k-card p-6 sm:p-8">
              <p className="k-overline mb-5">Your details</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FieldBox label="Your name"><Input required autoComplete="name" value={form.customer_name} onChange={(e) => setField("customer_name", e.target.value)} className="k-input" data-testid="checkout-name" /></FieldBox>
                <FieldBox label="Phone"><Input required type="tel" autoComplete="tel" minLength={10} pattern="[+0-9 ]{10,18}" title="Enter digits, spaces and an optional country code" value={form.phone} onChange={(e) => setField("phone", e.target.value)} className="k-input" data-testid="checkout-phone" /></FieldBox>
                <div className="sm:col-span-2"><FieldBox label="Email"><Input required autoComplete="email" type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} className="k-input" data-testid="checkout-email" /></FieldBox></div>
              </div>
              <div className="mt-4"><FieldBox label="Shipping address"><Textarea required autoComplete="street-address" value={form.shipping_address} onChange={(e) => setField("shipping_address", e.target.value)} className="k-input min-h-[90px]" data-testid="checkout-address" /></FieldBox></div>
            </div>

            {/* Gift details */}
            <div className="k-card p-6 sm:p-8">
              <p className="k-overline mb-5">Gift details</p>
              <div className="grid grid-cols-1 gap-4">
                <FieldBox label="Recipient name"><Input value={form.recipient_name} onChange={(e) => setField("recipient_name", e.target.value)} className="k-input" data-testid="checkout-recipient" /></FieldBox>
                <FieldBox label="Gift note (optional)"><Textarea value={form.gift_note} onChange={(e) => setField("gift_note", e.target.value)} className="k-input min-h-[80px]" data-testid="checkout-gift-note" /></FieldBox>
              </div>
            </div>

            {/* Payment */}
            <div className="k-card p-6 sm:p-8">
              <p className="k-overline mb-3">Payment</p>
              <div className="flex items-start gap-3 rounded-sm border border-lineSoft bg-cream p-4">
                <Lock className="text-chocolate mt-0.5 shrink-0" size={18} />
                <div>
                  <p className="text-sm text-ink font-medium">Secure payment by Razorpay</p>
                  <p className="text-xs text-inkMute mt-0.5">UPI, cards, netbanking and wallets. Payment opens in the Razorpay checkout window.</p>
                </div>
              </div>
            </div>
          </form>

          {/* Summary */}
          <div className="k-card p-6 self-start lg:sticky lg:top-24">
            <p className="k-overline mb-4">Order summary</p>
            <div className="text-sm text-ink space-y-2">
              <div className="flex justify-between"><span>{meta.products[product].label}</span><span>₹{meta.products[product].price.toLocaleString('en-IN')}</span></div>
              {totals?.breakdown?.map((b, i) => (
                <div key={i} className="flex justify-between text-inkMute"><span>{b.name}</span><span>₹{b.price.toLocaleString('en-IN')}</span></div>
              ))}
              {totals?.discount > 0 && (
                <div className="flex justify-between text-chocolate"><span>Discount</span><span>−₹{totals.discount.toLocaleString('en-IN')}</span></div>
              )}
              <div className="k-divider my-3" />
              <div className="flex justify-between text-lg font-medium" aria-live="polite" data-testid="checkout-total"><span>Total</span><span>{totals ? `₹${totals.final_total.toLocaleString('en-IN')}` : "Updating…"}</span></div>
            </div>
            <div className="mt-5">
              <Label htmlFor="coupon-code" className="k-label">Coupon code (optional)</Label>
              <Input id="coupon-code" value={coupon} onChange={(e) => setCoupon(e.target.value)} className="k-input" placeholder="Enter your code" data-testid="coupon-input" />
              {coupon.trim() && totals && <p role="status" className="text-xs text-inkMute mt-2" data-testid="coupon-status">{totals.discount > 0 ? "Coupon applied." : "This code has not added a discount."}</p>}
            </div>
            {totalsError && <p role="alert" className="field-error" data-testid="checkout-totals-error">Couldn’t confirm the total. Please try again before paying.</p>}
            <button type="submit" form="checkout-form" disabled={submitting || !totals} className="btn-primary w-full mt-6 disabled:opacity-60" data-testid="place-order-btn">
              {submitting ? "Opening payment…" : (<>Pay ₹{(totals?.final_total ?? totals?.total_price ?? meta.products[product].price).toLocaleString('en-IN')} <ArrowRight size={16} className="ml-1.5" /></>)}
            </button>
            <p className="text-xs text-inkMute mt-3" data-testid="checkout-policy-note">Questions about delivery or changes? <Link to="/contact" className="underline" data-testid="checkout-contact">Contact us</Link> before paying. Read our <Link to="/privacy" className="underline" data-testid="checkout-privacy">Privacy Policy</Link>.</p>
          </div>
        </div>
      </main>
    </div>
  );
}

function FieldBox({ label, children }) {
  const id = useId();
  return (
    <div>
      <Label htmlFor={id} className="k-label">{label}{children.props.required ? " *" : ""}</Label>
      {cloneElement(children, { id })}
    </div>
  );
}
